import { authedFetch } from "../../../common/http_service";
import { getToken } from "../../../common/environment_config";
import { getRecentActions } from "./recentActions";

// HttpAgent `fetch` for the AI chat.
//
// The agent is created once (ChatPanel's useMemo) and lives for the whole
// editing session, while the access token is renewed under it every ~48 min —
// so the Authorization header is NOT part of the agent's static headers. It is
// set here, per request, by authedFetch, which also renews the session and
// re-sends the message once on a 401. A token baked in at agent creation made
// the chat 401 an hour into the session.
//
// Also injects the per-request context headers (selection + recent edits):
// they change as the user works, and reading the window globals here keeps
// them current without re-creating the agent. Best-effort — a bad value never
// breaks the request.
//
// Header names come out lower-cased (HTTP header names are case-insensitive);
// that also guarantees a single Authorization header after authedFetch adds
// its own.
function toPlainHeaders(headers) {
  const out = {};
  if (!headers) return out;
  if (typeof headers.forEach === "function" && !Array.isArray(headers)) {
    headers.forEach((value, key) => {
      out[String(key).toLowerCase()] = value;
    });
    return out;
  }
  const entries = Array.isArray(headers) ? headers : Object.entries(headers);
  entries.forEach(([key, value]) => {
    out[String(key).toLowerCase()] = value;
  });
  return out;
}

import { latestPlayheadSec, startPlayheadTracking } from "./playheadTracker";

export function agUiFetch(url, init) {
  // No resolvable AG-UI endpoint (see ChatPanel's resolveAgUiUrl) — reject
  // instead of firing a request. Surfaces as the message's normal
  // error/incomplete state rather than hitting an assumed localhost URL.
  if (!url) return Promise.reject(new Error("AG-UI endpoint not configured"));

  const next = { ...(init || {}) };
  const headers = toPlainHeaders(next.headers);
  delete headers.authorization;
  try {
    // The playhead rides on the selection descriptor rather than a header of
    // its own: it answers the same question ("what is the user pointing at"),
    // and a motion edit cannot be placed without it. Read at SEND time, not at
    // selection time — the playhead moves while the user types.
    startPlayheadTracking();
    const sel = typeof window !== "undefined" ? window.__aideaSelectedElement : undefined;
    const playheadSec = latestPlayheadSec();
    // A motion scene being on screen is set by the plugin itself while it is
    // mounted. The agent needs it: with nothing selected the descriptor is
    // otherwise empty, and "make this house's roof red" routes to the image
    // editor on a scene that holds no images at all.
    const motion = typeof window !== "undefined" ? window.__aideaMotionScene : undefined;
    const descriptor = {
      ...(sel && typeof sel === "object" ? sel : {}),
      ...(typeof playheadSec === "number" ? { playheadSec: Number(playheadSec.toFixed(2)) } : {}),
      ...(motion && motion.boxId ? { hasMotionScene: true } : {}),
    };
    if (Object.keys(descriptor).length) {
      headers["x-aidea-selected-element"] = JSON.stringify(descriptor);
    }
    const recent = getRecentActions();
    if (recent && recent.length) {
      let payload = JSON.stringify(recent.map((r) => r.text));
      if (payload.length > 1024) payload = payload.slice(0, 1024);
      headers["x-aidea-recent-actions"] = payload;
    }
  } catch {
    /* context headers are optional */
  }
  next.headers = headers;

  // No token: the unauthenticated echo-server path — no Authorization header.
  if (!getToken()) return window.fetch(url, next);
  return authedFetch(url, next);
}
